import { memo, useState } from 'react';
import { AcoesTriagem, BotaoManter } from '@/components/kanban/triagem/AcoesTriagem';
import { formatarDocumento } from '@/components/kanban/triagem/segmentoTriagem';
import { ManterComNotaModal } from '@/components/kanban/ManterComNotaModal';
import { diasParadoNaFila } from '@/components/kanban/triagem/diasParadoNaFila';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Check, Eye, Loader2, Hourglass } from 'lucide-react';
import { CancelarItemTriagemButton } from '@/components/kanban/triagem/triagemPedidoUtils';
import { CopyOnClick } from '@/components/planilha/CopyOnClick';
import { usePainelPedido } from '@/contexts/PainelPedidoContext';
import { ErroInputJustificativaDialog } from './ErroInputJustificativaDialog';
import type { ItemTriagemCotacao } from './useTriagemCotacao';
import { PrazoSlaBadge } from '@/components/kanban/PrazoSlaBadge';
import { TRIAGEM_BTN, TRIAGEM_BTN_PRIMARIO } from '@/components/kanban/triagem/triagemTabela';

interface TriagemCotacaoRowProps {
  item: ItemTriagemCotacao;
  onMoverParaAnaliseManual: (itemId: string) => void;
  onMoverParaErroInput: (itemId: string, textoOperadora: string) => Promise<unknown>;
  onMoverParaInspecaoConcluida: (itemId: string) => void;
  onMoverParaFinal: (itemId: string) => void;
  /** Esta cotação está esperando o banco (só ela; o resto da fila fica livre). */
  ocupada: boolean;
  onAfterCancel?: () => void;
}


export const TriagemCotacaoRow = memo(function TriagemCotacaoRow({
  item,
  onMoverParaAnaliseManual,
  onMoverParaErroInput,
  onMoverParaInspecaoConcluida,
  onMoverParaFinal,
  ocupada,
  onAfterCancel,
}: TriagemCotacaoRowProps) {
  const [manterModalOpen, setManterModalOpen] = useState(false);
  const [erroInputOpen, setErroInputOpen] = useState(false);
  const cnpjFormatado = formatarDocumento(item.documento || item.cnpj);
  const [acaoEmCurso, setAcaoEmCurso] = useState<'analise' | 'erro' | 'aprovado' | 'manter' | null>(null);
  const isProcessing = ocupada;
  const isMovingToAnaliseManual = ocupada && acaoEmCurso === 'analise';
  const isMovingToErroInput = ocupada && acaoEmCurso === 'erro';
  const isMovingToInspecaoConcluida = ocupada && acaoEmCurso === 'aprovado';
  const isMovingToFinal = ocupada && acaoEmCurso === 'manter';
  
  const { open: openPedido } = usePainelPedido();

  const handleOpenPedido = (e: React.MouseEvent) => {
    e.stopPropagation();
    openPedido(item.pedido_id, 'visao360');
  };

  return (
    <tr className={`box-border h-[33px] border-b border-border/30 hover:bg-muted/30 transition-colors ${!item.is_first_of_cnpj ? 'bg-muted/10' : ''}`}>
      {/* CNPJ - só mostra na primeira linha do grupo */}
      <td className="px-2 py-0 align-middle">
        {item.is_first_of_cnpj ? (
          <CopyOnClick 
            value={item.documento || item.cnpj} 
            className="tabular-nums text-xs"
          >
            {cnpjFormatado}
          </CopyOnClick>
        ) : null}
      </td>

      {/* RAZÃO SOCIAL - só mostra na primeira linha do grupo */}
      <td className="px-2 py-0 align-middle">
        {item.is_first_of_cnpj ? (
          <span className="text-xs text-muted-foreground truncate block max-w-[140px]" title={item.razao_social}>
            {item.razao_social || '-'}
          </span>
        ) : null}
      </td>
      
      {/* Cotação */}
      <td className="px-2 py-0 align-middle">
        {item.numero_cotacao_operadora ? (
          <CopyOnClick 
            value={item.numero_cotacao_operadora} 
            className="text-xs font-medium text-primary"
          >
            {item.numero_cotacao_operadora}
          </CopyOnClick>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </td>
      
      {/* Pedido */}
      <td className="px-2 py-0 align-middle">
        <button
          onClick={handleOpenPedido}
          className="text-xs font-medium text-primary hover:underline cursor-pointer"
          title="Abrir detalhes do pedido"
        >
          {item.numero_pedido}
        </button>
      </td>
      
      {/* Produto */}
      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground truncate block max-w-[80px]" title={item.produto}>
          {item.produto || '-'}
        </span>
      </td>

      {/* QTD */}
      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">{item.quantidade || '-'}</span>
      </td>

      {/* VALOR */}
      <td className="px-2 py-0 align-middle">
        <span className="text-xs text-muted-foreground">
          {/* 🐛 Lia `plano_valor`, que vem NULO em 39 de 39 itens desta fila — a coluna
            aparecia vazia e o desenho concluiu "coluna morta, pode sair". O valor
            existe: está em `subtotal` (o mesmo que o hook usa no total do
            cabeçalho, com fallback plano_valor × quantidade). */}
          {item.subtotal ? `R$ ${item.subtotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
        </span>
      </td>

      {/* Tempo na fila — Validação Pendente: prazo 10h */}
      <td className="px-2 py-0 align-middle">
        <PrazoSlaBadge
          pedidoId={item.pedido_id}
          coluna="VALIDAÇÃO PENDENTE"
          entradaColunaEm={item.entrada_coluna_em}
          prazoHoras={10}
        />
      </td>
      
      {/* Ações — ordem padrão: Manter | avançar | desvios | analisar | cancelar */}
      <td className="px-2 py-0 align-middle">
        <AcoesTriagem
          manter={
            <BotaoManter
              title="Manter — consultei e ainda não mudou nada. Vai para o fim da fila."
              onClick={() => setManterModalOpen(true)}
              disabled={isProcessing}
              carregando={isMovingToFinal}
            />
          }
          avancar={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN_PRIMARIO}
              title="Aprovado - Crédito OK, vai para Pré-Ativo"
              onClick={() => { setAcaoEmCurso('aprovado'); onMoverParaInspecaoConcluida(item.item_id); }}
              disabled={isProcessing}
            >
              {isMovingToInspecaoConcluida ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </Button>
          }
          desvios={
            <>
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN}
                title="Análise Manual - Requer revisão de crédito"
                onClick={() => { setAcaoEmCurso('analise'); onMoverParaAnaliseManual(item.item_id); }}
                disabled={isProcessing}
              >
                {isMovingToAnaliseManual ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Hourglass className="h-3.5 w-3.5" />
                )}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className={TRIAGEM_BTN}
                title="Erro de Input - justificar com o texto da operadora"
                onClick={() => setErroInputOpen(true)}
                disabled={isProcessing}
              >
                {isMovingToErroInput ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
              </Button>
            </>
          }
          ferramentas={
            <Button
              size="icon"
              variant="ghost"
              className={TRIAGEM_BTN}
              title="Analisar — Abrir Painel do Pedido (Pedido 360)"
              onClick={() => openPedido(item.pedido_id, 'visao360')}
            >
              <Eye className="h-3 w-3" />
            </Button>
          }
          cancelar={
            <CancelarItemTriagemButton
              pedidoId={item.pedido_id}
              itens={[{ item_id: item.item_id, produto: item.produto || item.numero_pedido, quantidade: item.quantidade || 1, subtotal: item.subtotal || 0 }]}
              disabled={isProcessing}
              disabledTitle="Aguarde a ação em andamento"
              onAfterCancel={() => onAfterCancel?.()}
            />
          }
        />
        <ManterComNotaModal
          open={manterModalOpen}
          onOpenChange={setManterModalOpen}
          pedidoId={item.pedido_id}
          clienteId=""
          onConfirm={() => { setAcaoEmCurso('manter'); onMoverParaFinal(item.item_id); }}
          justificarFila={{
            itemId: item.item_id,
            coluna: 'VALIDAÇÃO PENDENTE',
            numeroPedido: item.numero_pedido,
            razaoSocial: item.razao_social,
            justificativaAtual: null,
            diasParado: diasParadoNaFila(item.entrada_coluna_em),
          }}
        />
        <ErroInputJustificativaDialog
          open={erroInputOpen}
          onOpenChange={setErroInputOpen}
          numeroPedido={item.numero_pedido}
          razaoSocial={item.razao_social}
          cotacao={item.numero_cotacao_operadora}
          isLoading={isMovingToErroInput}
          onConfirm={async (texto) => {
            try {
              setAcaoEmCurso('erro');
              await onMoverParaErroInput(item.item_id, texto);
              setErroInputOpen(false);
            } catch {
              /* toast no hook */
            }
          }}
        />
      </td>
    </tr>
  );
});
